"use client";
import React from 'react';
import { useCopilotAction } from "@copilotkit/react-core";
import { LoadingView } from '../ui/CalendarMeetingCard';
import { MapPin, Users, Calendar, Check, AlertCircle } from 'lucide-react';
// Helper function to check if we're in a compose appointment
const isComposeAppointment = () => {
    return !!(typeof Office !== 'undefined' &&
        Office.context &&
        Office.context.mailbox &&
        Office.context.mailbox.item &&
        Office.context.mailbox.item.itemType === 'appointment');
};
// Helper function to format attendees for display
const formatAttendees = (attendees) => {
    if (!attendees || attendees.length === 0)
        return '';
    return attendees.map(attendee => attendee.emailAddress || attendee.displayName || attendee).join(', ');
};
// Helper function to parse email addresses
const parseEmailAddresses = (emailString) => {
    return emailString.split(',').map(email => {
        var _a, _b, _c;
        const trimmed = email.trim();
        // Handle both "Name <email@domain.com>" and "email@domain.com" formats
        const emailMatch = trimmed.match(/([^<>]*)<([^<>]+)>|([^<>,]+)/);
        if (emailMatch) {
            const name = (_a = emailMatch[1]) === null || _a === void 0 ? void 0 : _a.trim();
            const email = ((_b = emailMatch[2]) === null || _b === void 0 ? void 0 : _b.trim()) || ((_c = emailMatch[3]) === null || _c === void 0 ? void 0 : _c.trim());
            return {
                displayName: name || email,
                emailAddress: email
            };
        }
        return {
            displayName: trimmed,
            emailAddress: trimmed
        };
    });
};
export function ActionsOutlook() {
    const [currentMeetingInfo, setCurrentMeetingInfo] = React.useState(null);
    // Get current meeting information
    useCopilotAction({
        name: "getCurrentMeetingInfo",
        description: "Get information about the current appointment/meeting being composed in Outlook",
        parameters: [],
        handler: async () => {
            return new Promise((resolve) => {
                if (!isComposeAppointment()) {
                    resolve({
                        _error: true,
                        _message: "This action only works when composing an appointment/meeting in Outlook"
                    });
                    return;
                }
                const meetingInfo = {};
                let completed = 0;
                const total = 6; // number of async operations
                const checkComplete = () => {
                    completed++;
                    if (completed === total) {
                        setCurrentMeetingInfo(meetingInfo);
                        resolve({
                            _success: true,
                            meetingInfo
                        });
                    }
                };
                const item = Office.context.mailbox.item;
                if (!item) {
                    resolve({
                        _error: true,
                        _message: "No appointment item found"
                    });
                    return;
                }
                // Get subject
                item.subject.getAsync((result) => {
                    meetingInfo.subject = result.status === Office.AsyncResultStatus.Succeeded ? result.value : '';
                    checkComplete();
                });
                // Get location
                item.location.getAsync((result) => {
                    meetingInfo.location = result.status === Office.AsyncResultStatus.Succeeded ? result.value : '';
                    checkComplete();
                });
                // Get start time
                item.start.getAsync((result) => {
                    meetingInfo.startTime = result.status === Office.AsyncResultStatus.Succeeded ? result.value : null;
                    checkComplete();
                });
                // Get end time
                item.end.getAsync((result) => {
                    meetingInfo.endTime = result.status === Office.AsyncResultStatus.Succeeded ? result.value : null;
                    checkComplete();
                });
                // Get required attendees
                item.requiredAttendees.getAsync((result) => {
                    meetingInfo.requiredAttendees = result.status === Office.AsyncResultStatus.Succeeded ? result.value : [];
                    checkComplete();
                });
                // Get optional attendees
                item.optionalAttendees.getAsync((result) => {
                    meetingInfo.optionalAttendees = result.status === Office.AsyncResultStatus.Succeeded ? result.value : [];
                    checkComplete();
                });
            });
        },
        render: ({ status, result }) => {
            if (status === "executing") {
                return React.createElement(LoadingView, null);
            }
            if (status === "complete" && result) {
                if (result._error) {
                    return (React.createElement("div", { className: "bg-red-50 border border-red-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2" },
                            React.createElement(AlertCircle, { className: "w-5 h-5 text-red-600" }),
                            React.createElement("p", { className: "text-red-800" }, result._message))));
                }
                if (result._success && result.meetingInfo) {
                    const info = result.meetingInfo;
                    return (React.createElement("div", { className: "bg-blue-50 border border-blue-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2 mb-3" },
                            React.createElement(Calendar, { className: "w-5 h-5 text-blue-600" }),
                            React.createElement("h4", { className: "font-medium text-blue-900" }, "Current Meeting Information")),
                        React.createElement("div", { className: "space-y-2 text-sm text-blue-800" },
                            React.createElement("p", null,
                                React.createElement("strong", null, "Subject:"),
                                " ",
                                info.subject || 'No subject'),
                            React.createElement("p", null,
                                React.createElement("strong", null, "Location:"),
                                " ",
                                info.location || 'No location set'),
                            React.createElement("p", null,
                                React.createElement("strong", null, "Start:"),
                                " ",
                                info.startTime ? new Date(info.startTime).toLocaleString() : 'Not set'),
                            React.createElement("p", null,
                                React.createElement("strong", null, "End:"),
                                " ",
                                info.endTime ? new Date(info.endTime).toLocaleString() : 'Not set'),
                            React.createElement("p", null,
                                React.createElement("strong", null, "Required Attendees:"),
                                " ",
                                formatAttendees(info.requiredAttendees) || 'None'),
                            React.createElement("p", null,
                                React.createElement("strong", null, "Optional Attendees:"),
                                " ",
                                formatAttendees(info.optionalAttendees) || 'None'))));
                }
            }
            return React.createElement("div", null);
        },
    });
    // Set meeting location
    useCopilotAction({
        name: "setMeetingLocation",
        description: "Set the location for the current appointment/meeting being composed in Outlook",
        parameters: [
            {
                name: "location",
                type: "string",
                description: "The location for the meeting (e.g., 'Conference Room A', 'Microsoft Teams Meeting', 'Zoom', etc.)",
                required: true,
            },
        ],
        handler: async ({ location }) => {
            return new Promise((resolve) => {
                if (!isComposeAppointment()) {
                    resolve({
                        _error: true,
                        _message: "This action only works when composing an appointment/meeting in Outlook"
                    });
                    return;
                }
                const item = Office.context.mailbox.item;
                if (!item) {
                    resolve({
                        _error: true,
                        _message: "No appointment item found"
                    });
                    return;
                }
                item.location.setAsync(location, (result) => {
                    var _a;
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        resolve({
                            _success: true,
                            location,
                            _message: `Location set to: ${location}`
                        });
                    }
                    else {
                        resolve({
                            _error: true,
                            _message: `Failed to set location: ${((_a = result.error) === null || _a === void 0 ? void 0 : _a.message) || 'Unknown error'}`
                        });
                    }
                });
            });
        },
        render: ({ status, result }) => {
            if (status === "executing") {
                return React.createElement(LoadingView, null);
            }
            if (status === "complete" && result) {
                if (result._error) {
                    return (React.createElement("div", { className: "bg-red-50 border border-red-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2" },
                            React.createElement(AlertCircle, { className: "w-5 h-5 text-red-600" }),
                            React.createElement("p", { className: "text-red-800" }, result._message))));
                }
                if (result._success) {
                    return (React.createElement("div", { className: "bg-green-50 border border-green-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2" },
                            React.createElement(Check, { className: "w-5 h-5 text-green-600" }),
                            React.createElement("div", null,
                                React.createElement("p", { className: "text-green-800 font-medium" }, "\u2705 Location Updated!"),
                                React.createElement("div", { className: "flex items-center space-x-2 mt-2" },
                                    React.createElement(MapPin, { className: "w-4 h-4 text-green-600" }),
                                    React.createElement("p", { className: "text-green-700" }, result.location))))));
                }
            }
            return React.createElement("div", null);
        },
    });
    // Add required attendees
    useCopilotAction({
        name: "addRequiredAttendees",
        description: "Add required attendees to the current appointment/meeting being composed in Outlook",
        parameters: [
            {
                name: "attendees",
                type: "string",
                description: "Email addresses of required attendees, comma-separated (e.g., 'john@company.com, jane@company.com')",
                required: true,
            },
        ],
        handler: async ({ attendees }) => {
            return new Promise((resolve) => {
                if (!isComposeAppointment()) {
                    resolve({
                        _error: true,
                        _message: "This action only works when composing an appointment/meeting in Outlook"
                    });
                    return;
                }
                const item = Office.context.mailbox.item;
                if (!item) {
                    resolve({
                        _error: true,
                        _message: "No appointment item found"
                    });
                    return;
                }
                const parsedAttendees = parseEmailAddresses(attendees);
                item.requiredAttendees.addAsync(parsedAttendees, (result) => {
                    var _a;
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        resolve({
                            _success: true,
                            attendees: parsedAttendees,
                            _message: `Added ${parsedAttendees.length} required attendee(s)`
                        });
                    }
                    else {
                        resolve({
                            _error: true,
                            _message: `Failed to add attendees: ${((_a = result.error) === null || _a === void 0 ? void 0 : _a.message) || 'Unknown error'}`
                        });
                    }
                });
            });
        },
        render: ({ status, result }) => {
            if (status === "executing") {
                return React.createElement(LoadingView, null);
            }
            if (status === "complete" && result) {
                if (result._error) {
                    return (React.createElement("div", { className: "bg-red-50 border border-red-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2" },
                            React.createElement(AlertCircle, { className: "w-5 h-5 text-red-600" }),
                            React.createElement("p", { className: "text-red-800" }, result._message))));
                }
                if (result._success && result.attendees) {
                    return (React.createElement("div", { className: "bg-green-50 border border-green-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2 mb-2" },
                            React.createElement(Check, { className: "w-5 h-5 text-green-600" }),
                            React.createElement("p", { className: "text-green-800 font-medium" }, "\u2705 Required Attendees Added!")),
                        React.createElement("div", { className: "space-y-1" }, result.attendees.map((attendee, index) => (React.createElement("div", { key: index, className: "flex items-center space-x-2" },
                            React.createElement(Users, { className: "w-4 h-4 text-green-600" }),
                            React.createElement("p", { className: "text-green-700" },
                                attendee.displayName,
                                " (",
                                attendee.emailAddress,
                                ")")))))));
                }
            }
            return React.createElement("div", null);
        },
    });
    // Add optional attendees
    useCopilotAction({
        name: "addOptionalAttendees",
        description: "Add optional attendees to the current appointment/meeting being composed in Outlook",
        parameters: [
            {
                name: "attendees",
                type: "string",
                description: "Email addresses of optional attendees, comma-separated (e.g., 'john@company.com, jane@company.com')",
                required: true,
            },
        ],
        handler: async ({ attendees }) => {
            return new Promise((resolve) => {
                if (!isComposeAppointment()) {
                    resolve({
                        _error: true,
                        _message: "This action only works when composing an appointment/meeting in Outlook"
                    });
                    return;
                }
                const item = Office.context.mailbox.item;
                if (!item) {
                    resolve({
                        _error: true,
                        _message: "No appointment item found"
                    });
                    return;
                }
                const parsedAttendees = parseEmailAddresses(attendees);
                item.optionalAttendees.addAsync(parsedAttendees, (result) => {
                    var _a;
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        resolve({
                            _success: true,
                            attendees: parsedAttendees,
                            _message: `Added ${parsedAttendees.length} optional attendee(s)`
                        });
                    }
                    else {
                        resolve({
                            _error: true,
                            _message: `Failed to add attendees: ${((_a = result.error) === null || _a === void 0 ? void 0 : _a.message) || 'Unknown error'}`
                        });
                    }
                });
            });
        },
        render: ({ status, result }) => {
            if (status === "executing") {
                return React.createElement(LoadingView, null);
            }
            if (status === "complete" && result) {
                if (result._error) {
                    return (React.createElement("div", { className: "bg-red-50 border border-red-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2" },
                            React.createElement(AlertCircle, { className: "w-5 h-5 text-red-600" }),
                            React.createElement("p", { className: "text-red-800" }, result._message))));
                }
                if (result._success && result.attendees) {
                    return (React.createElement("div", { className: "bg-blue-50 border border-blue-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2 mb-2" },
                            React.createElement(Check, { className: "w-5 h-5 text-blue-600" }),
                            React.createElement("p", { className: "text-blue-800 font-medium" }, "\u2705 Optional Attendees Added!")),
                        React.createElement("div", { className: "space-y-1" }, result.attendees.map((attendee, index) => (React.createElement("div", { key: index, className: "flex items-center space-x-2" },
                            React.createElement(Users, { className: "w-4 h-4 text-blue-600" }),
                            React.createElement("p", { className: "text-blue-700" },
                                attendee.displayName,
                                " (",
                                attendee.emailAddress,
                                ")")))))));
                }
            }
            return React.createElement("div", null);
        },
    });
    // Set meeting subject
    useCopilotAction({
        name: "setMeetingSubject",
        description: "Set the subject/title for the current appointment/meeting being composed in Outlook",
        parameters: [
            {
                name: "subject",
                type: "string",
                description: "The subject/title for the meeting",
                required: true,
            },
        ],
        handler: async ({ subject }) => {
            return new Promise((resolve) => {
                if (!isComposeAppointment()) {
                    resolve({
                        _error: true,
                        _message: "This action only works when composing an appointment/meeting in Outlook"
                    });
                    return;
                }
                const item = Office.context.mailbox.item;
                if (!item) {
                    resolve({
                        _error: true,
                        _message: "No appointment item found"
                    });
                    return;
                }
                item.subject.setAsync(subject, (result) => {
                    var _a;
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        resolve({
                            _success: true,
                            subject,
                            _message: `Subject set to: ${subject}`
                        });
                    }
                    else {
                        resolve({
                            _error: true,
                            _message: `Failed to set subject: ${((_a = result.error) === null || _a === void 0 ? void 0 : _a.message) || 'Unknown error'}`
                        });
                    }
                });
            });
        },
        render: ({ status, result }) => {
            if (status === "executing") {
                return React.createElement(LoadingView, null);
            }
            if (status === "complete" && result) {
                if (result._error) {
                    return (React.createElement("div", { className: "bg-red-50 border border-red-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2" },
                            React.createElement(AlertCircle, { className: "w-5 h-5 text-red-600" }),
                            React.createElement("p", { className: "text-red-800" }, result._message))));
                }
                if (result._success) {
                    return (React.createElement("div", { className: "bg-green-50 border border-green-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2" },
                            React.createElement(Check, { className: "w-5 h-5 text-green-600" }),
                            React.createElement("div", null,
                                React.createElement("p", { className: "text-green-800 font-medium" }, "\u2705 Subject Updated!"),
                                React.createElement("p", { className: "text-green-700 mt-1" }, result.subject)))));
                }
            }
            return React.createElement("div", null);
        },
    });
    // Set meeting time
    useCopilotAction({
        name: "setMeetingTime",
        description: "Set the start and end time for the current appointment/meeting being composed in Outlook",
        parameters: [
            {
                name: "startTime",
                type: "string",
                description: "Start time in ISO format (e.g., '2025-01-15T14:00:00'). Use getCurrentDateTime to get current date first!",
                required: true,
            },
            {
                name: "endTime",
                type: "string",
                description: "End time in ISO format (e.g., '2025-01-15T15:00:00'). Use getCurrentDateTime to get current date first!",
                required: true,
            },
        ],
        handler: async ({ startTime, endTime }) => {
            return new Promise((resolve) => {
                if (!isComposeAppointment()) {
                    resolve({
                        _error: true,
                        _message: "This action only works when composing an appointment/meeting in Outlook"
                    });
                    return;
                }
                const item = Office.context.mailbox.item;
                if (!item) {
                    resolve({
                        _error: true,
                        _message: "No appointment item found"
                    });
                    return;
                }
                const startDate = new Date(startTime);
                const endDate = new Date(endTime);
                if (endDate <= startDate) {
                    resolve({
                        _error: true,
                        _message: "End time must be after start time"
                    });
                    return;
                }
                let completed = 0;
                let hasError = false;
                const checkComplete = () => {
                    completed++;
                    if (completed === 2 && !hasError) {
                        resolve({
                            _success: true,
                            startTime,
                            endTime,
                            _message: `Meeting time set: ${startDate.toLocaleString()} - ${endDate.toLocaleString()}`
                        });
                    }
                };
                // Set start time
                item.start.setAsync(startDate, (result) => {
                    var _a;
                    if (result.status !== Office.AsyncResultStatus.Succeeded) {
                        hasError = true;
                        resolve({
                            _error: true,
                            _message: `Failed to set start time: ${((_a = result.error) === null || _a === void 0 ? void 0 : _a.message) || 'Unknown error'}`
                        });
                    }
                    else {
                        checkComplete();
                    }
                });
                // Set end time
                item.end.setAsync(endDate, (result) => {
                    var _a;
                    if (result.status !== Office.AsyncResultStatus.Succeeded) {
                        hasError = true;
                        resolve({
                            _error: true,
                            _message: `Failed to set end time: ${((_a = result.error) === null || _a === void 0 ? void 0 : _a.message) || 'Unknown error'}`
                        });
                    }
                    else {
                        checkComplete();
                    }
                });
            });
        },
        render: ({ status, result }) => {
            if (status === "executing") {
                return React.createElement(LoadingView, null);
            }
            if (status === "complete" && result) {
                if (result._error) {
                    return (React.createElement("div", { className: "bg-red-50 border border-red-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2" },
                            React.createElement(AlertCircle, { className: "w-5 h-5 text-red-600" }),
                            React.createElement("p", { className: "text-red-800" }, result._message))));
                }
                if (result._success) {
                    return (React.createElement("div", { className: "bg-green-50 border border-green-200 rounded-lg p-4 my-4" },
                        React.createElement("div", { className: "flex items-center space-x-2" },
                            React.createElement(Check, { className: "w-5 h-5 text-green-600" }),
                            React.createElement("div", null,
                                React.createElement("p", { className: "text-green-800 font-medium" }, "\u2705 Meeting Time Updated!"),
                                React.createElement("div", { className: "flex items-center space-x-2 mt-2" },
                                    React.createElement(Calendar, { className: "w-4 h-4 text-green-600" }),
                                    React.createElement("p", { className: "text-green-700" },
                                        new Date(result.startTime).toLocaleString(),
                                        " - ",
                                        new Date(result.endTime).toLocaleString()))))));
                }
            }
            return React.createElement("div", null);
        },
    });
    return null; // This component doesn't render anything directly
}
